/*
 * Identifying a basket by what is in it.
 *
 * The double-charge guard keys on the basket's reference, which is minted when
 * the payment screen opens. That catches a retry of one basket and cannot
 * catch a duplicate: ring the same order twice and there are two references,
 * so the agent sees two unrelated sales. A shop charged a customer 22,000
 * twice for one espresso that way.
 *
 * This is the other half of the key — what is being bought, rather than which
 * sale is buying it — so the agent can ask once before taking the money again.
 */

export interface FingerprintLine {
  variantId: string;
  quantity: number;
  discountRial: number;
}

/*
 * basketFingerprint is stable for the same contents and different for
 * anything else a cashier would consider a different sale.
 *
 * Sorted by variant, because the order items were scanned in is not part of
 * what the customer is buying — two cashiers ringing the same two coffees in
 * opposite orders are selling the same thing.
 *
 * Quantity is included at the same precision the sale posts, and per-line
 * discounts too: «two coffees, one of them half price» is not the same basket
 * as «two coffees».
 */
export function basketFingerprint(
  lines: FingerprintLine[],
  totalRial: number,
  basketDiscountRial: number,
): string {
  if (lines.length === 0) return '';

  const canonical = lines
    .map((l) => `${l.variantId}:${l.quantity.toFixed(3)}:${Math.round(l.discountRial)}`)
    .sort()
    .join('|');

  return `v1.${hash(`${canonical}#${Math.round(totalRial)}#${Math.round(basketDiscountRial)}`)}`;
}

/*
 * A short, stable hash.
 *
 * FNV-1a, 32-bit, written out because the browser's crypto.subtle digest is
 * async and this is read on a click path. A collision here would mean asking
 * about a basket that is not actually a duplicate — the cashier says "yes,
 * different customer" and the sale proceeds — so the cost of one is a question,
 * not a lost or doubled payment.
 */
function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // The FNV prime, via shifts: a plain multiply overflows past 2^53.
    h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
